import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, CheckCircle2, MapPin, ClipboardCheck, TrendingUp, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

function Stat({ label, value, icon: Icon, tone = "neutral", testid }) {
  const toneColor = {
    neutral: "var(--fb-primary)",
    success: "var(--fb-success)",
    warning: "var(--fb-warning)",
    accent: "var(--fb-accent)",
  }[tone];
  return (
    <div className="fb-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
             style={{ background: `${toneColor}15`, color: toneColor }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit", color: "var(--fb-text)" }}>
        {value ?? "—"}
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>
        {label}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/summary");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <div className="flex items-start sm:items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>
            Overview
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold" style={{ fontFamily: "Outfit" }}>
            Good morning, team.
          </h1>
          <p className="text-sm sm:text-base mt-2" style={{ color: "var(--fb-text-muted)" }}>
            Saturday service • {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </p>
        </div>
        <Link
          to="/staff/clients"
          data-testid="quick-checkin-btn"
          className="fb-btn-primary px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base inline-flex items-center gap-2"
        >
          <CheckCircle2 className="w-5 h-5" />
          Quick Check-In
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Clients" value={data?.total_clients} icon={Users} testid="stat-total-clients" />
        <Stat label="Address Verified" value={data?.total_address_verified} icon={MapPin} tone="success" testid="stat-verified" />
        <Stat label="Visits This Month" value={data?.visits_this_month} icon={Calendar} testid="stat-monthly-visits" />
        <Stat label="Pending Approvals" value={data?.pending_updates} icon={ClipboardCheck} tone="warning" testid="stat-pending" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="fb-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Visits — last 12 months</h3>
              <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Monthly check-in trend</p>
            </div>
            <TrendingUp className="w-5 h-5" style={{ color: "var(--fb-primary)" }} />
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthly_trend || []} margin={{ left: -20, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E4DD" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#5C615C", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#5C615C", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E6E4DD", borderRadius: 10 }}
                  cursor={{ fill: "rgba(5,47,102,0.05)" }}
                />
                <Bar dataKey="visits" radius={[6, 6, 0, 0]} fill="#052F66" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="fb-card p-6">
          <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: "Outfit" }}>Visits by Household Size</h3>
          <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>Last 12 months</p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_household_size || []} margin={{ left: -20, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E4DD" vertical={false} />
                <XAxis dataKey="size" tick={{ fill: "#5C615C", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#5C615C", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E6E4DD", borderRadius: 10 }} />
                <Bar dataKey="visits" radius={[6, 6, 0, 0]} fill="#D98A44">
                  {(data?.by_household_size || []).map((_, i) => (
                    <Cell key={i} fill={["#052F66", "#3A7D64", "#D98A44", "#C77526"][i % 4]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>}
    </div>
  );
}
